import React, { useState, useEffect, useRef } from "react";
import { gl_start, registerSceneHMR, setUniform } from "./webgl0.js";

function Scene() {
  let NS = 3;
  let NL = 3;

  this.vertexShader = `#version 300 es
in  vec3 aPos;
out vec3 vPos;
void main() {
   gl_Position = vec4(aPos, 1.0);
   vPos = aPos;
}`;

  this.fragmentShader = `#version 300 es
precision highp float;
uniform float uTime;
uniform vec3 uViewPoint;

uniform vec4 uS[` + NS + `]; // sphere (x,y,z,r)
uniform vec3 uC[` + NS + `]; // color of sphere

uniform vec3 uL[` + NL + `]; // light directions
uniform vec4 uLC[` + NL + `]; // color/intensity of light

in  vec3 vPos;
out vec4 fragColor;

vec2 raySphere(vec3 V, vec3 W, vec4 S) {
   V -= S.xyz;
   float b = dot(V, W);
   float d = b * b - dot(V, V) + S.w * S.w;
   if (d < 0.0)
      return vec2(1001.0, 1000.0);
   return vec2(-b - sqrt(d), -b + sqrt(d));
}

bool inShadow(vec3 P, vec3 L){
   for(int i = 0; i < ` + NS + `; i++) {
      vec2 tt = raySphere(P, L, uS[i]);
      if(tt.x < tt.y && tt.x > 0.0) {
         return true;
      }
   }
   return false;
}

vec3 shadeSphere(vec4 S, vec3 P, vec3 C) {
   vec3 N = (P - S.xyz) / S.w;
   vec3 shade = vec3(0.1); // ambient 

   for (int i = 0; i < ` + NL + `; i++) {
      if(!inShadow(P, uL[i])){
         shade += uLC[i].rgb * uLC[i].a * max(0.0, dot(N, uL[i]));
      }
   }
   
   return C * shade;
}

void main() {
   vec4 F = vec4(0.0);
   vec3 V = uViewPoint;
   vec3 W = normalize(vPos - V);
   float t = 100.0;
   
   for (int i = 0; i < ` + NS + `; i++) {
      vec2 tt = raySphere(V, W, uS[i]);
      if (tt.x < tt.y && tt.x > 0.0 && tt.x < t) {
         t = tt.x;
         vec3 P = V + t * W;
         F = vec4(shadeSphere(uS[i], P, uC[i]), 1.0);
      }
   }
   
   fragColor = vec4(sqrt(F.rgb), F.a);
}`;

  let startTime = Date.now() / 1000;

  let normalize = v => { 
    let s = Math.sqrt(v[0] * v[0] + v[1] * v[1] + v[2] * v[2]); 
    return [ v[0]/s, v[1]/s, v[2]/s ]; 
  }

  this.update = (viewPoint = [0, 0, 7]) => {
    let time = Date.now() / 1000 - startTime;
    setUniform("1f", "uTime", time);
    setUniform("3fv", "uViewPoint", viewPoint);

  let s = Math.sin(time * 2) * 0.5;
  let c = Math.cos(time * 2) * 0.5;

  // Smooth transition using smoothstep
  let t = s * 0.5 + 0.5;  // Convert sin range [-1,1] to [0,1]
  let smoothT = t * t * (3.0 - 2.0 * t);  // smoothstep formula

  let z1 = 1.0 * (1.0 - smoothT);  // smoothly goes from 0.8 to 0
  let z2 = 1.0 * smoothT;          // smoothly goes from 0 to 0.8

  setUniform("4fv", "uS", [
    c, 0, z1, 0.4,
    s, 0, z2, 0.4,
    // 0.0, 0.3 * s, 0.0, 0.4,
  ]);

  // Smoothly swap red channel between spheres
  let r1 = 1.0 * (1.0 - smoothT);  // first sphere: goes from 1.0 to 0.0
  let r2 = 1.0 * smoothT;          // second sphere: goes from 0.0 to 1.0

  setUniform("3fv", "uC", [
    r1, 0.0, 1.0,  // first sphere fades from white to cyan
    r2, 0.0, 1.0,  // second sphere fades from black to red
    // 0.5, 1.0, 0.5,
  ]);

  // upload normalized light directions (3 lights)
  setUniform("3fv", "uL", [
    normalize([1, 1, 1]),
    // normalize([-1, -1, -0.5]),
    // normalize([0, -1, 0])
  ].flat());

  // color/intensity for each light
  setUniform("4fv", "uLC", [
    1.0, 1.0, 1.0, 1.0,
    // 0.2, 0.15, 0.1, 1.0,
    // 1.0, 0.0, 0.0, 1.0
  ]);
  };
}

export default function WebGLCanvas() {
  const canvasRef = useRef(null);
  const [scene, setScene] = useState(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    try { if (typeof canvas.stopRender === 'function') canvas.stopRender(); } catch (e) {}

    const newScene = new Scene();

    gl_start(canvas, newScene);
    setScene(newScene);

    try { registerSceneHMR(module, canvas, () => new Scene()); } catch (e) { /* ignore in non-HMR env */ }
    return () => { try { if (typeof canvas.stopRender === 'function') canvas.stopRender(); } catch (e) {} };
  }, []);

  return (
    <div style = { { display: "flex", justifyContent: "center", gap : "1px" } }>
        <canvas ref={canvasRef} width={400} height={400} style={{ width : "400px", height : "400px", display: "block", margin: "0 auto", background: "black" }} />
        <pre className = "codeblock-shader">{ Scene.toString() }</pre>
    </div>
  );
}
